import { useEffect, useRef, useState } from 'react'
import { logout, fetchWithAuth } from '../utils/api'
import GPXViewerModal from './GPXViewerModal'
import GeoJSONViewerModal from './GeoJSONViewerModal'
import { Site, User, VersionResponse } from '../types'

export default function SettingsModal({ user, uptime, version, onClose }: { user: User; uptime?: string; version?: VersionResponse | null; onClose: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pwCurrent, setPwCurrent] = useState('')
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string>('')
  const [fileContent, setFileContent] = useState<string>('')
  const [fileBadge, setFileBadge] = useState<{ kind: 'xml' | 'gpx' | 'geojson' | 'invalid' | null; label: string; color: string; meta?: string } | null>(null)
  const [showGPXViewer, setShowGPXViewer] = useState(false)
  const [showGeoJSONViewer, setShowGeoJSONViewer] = useState(false)
  // Export/Import (Site GeoJSON)
  const [sites, setSites] = useState<Site[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState<number | ''>('')
  const [exportData, setExportData] = useState<any | null>(null)
  const [importMessage, setImportMessage] = useState<string | null>(null)
  const importFileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ;(async () => {
      const res = await fetchWithAuth('/api/sites').catch(() => null)
      if (res && res.ok) {
        const s = await res.json()
        setSites(s)
        if (s.length === 1) setSelectedSiteId(s[0].id)
      }
    })()
  }, [])

  const fetchExportPreview = async () => {
    setExportData(null)
    setImportMessage(null)
    if (!selectedSiteId) return
    const res = await fetchWithAuth(`/api/sites/${selectedSiteId}/geojson/export`).catch(() => null)
    if (res && res.ok) setExportData(await res.json())
  }

  const downloadExport = () => {
    if (!exportData) return
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `site-${selectedSiteId}-export.geojson`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportMessage(null)
    const f = e.target.files?.[0]
    if (!f) return
    try {
      const text = await f.text()
      const obj = JSON.parse(text)
      if (!selectedSiteId) { setImportMessage('Select a site first'); return }
      const res = await fetchWithAuth(`/api/sites/${selectedSiteId}/geojson/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(obj)
      }).catch(() => null)
      if (res && res.ok) {
        const data = await res.json()
        setImportMessage(`Imported: ${data.insertedAreas} areas, ${data.insertedAssets} assets; Skipped: ${data.skippedAreas} areas, ${data.skippedAssets} assets`)
        // refresh preview after import
        fetchExportPreview()
      } else {
        const err = await res?.json().catch(() => ({} as any))
        setImportMessage(err?.error || 'Import failed')
      }
    } catch {
      setImportMessage('Invalid file or JSON')
    } finally {
      if (importFileRef.current) importFileRef.current.value = ''
    }
  }

  const openFile = () => fileInputRef.current?.click()
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const text = await file.text()
    setFileName(file.name)
    setFileContent(text)
    // Validate based on content; but require correct extension first
    const lower = file.name.toLowerCase()
    const extOk = lower.endsWith('.gpx') || lower.endsWith('.geojson')
    if (!extOk) {
      setFileBadge({ kind: 'invalid', label: 'Unsupported extension', color: '#ef4444' })
      e.target.value = ''
      return
    }
    // Try GPX/XML first if .gpx
    if (lower.endsWith('.gpx')) {
      const res = validateGPX(text)
      if (res.isXML && res.isGPX) setFileBadge({ kind: 'gpx', label: 'GPX', color: '#10b981', meta: res.meta })
      else if (res.isXML) setFileBadge({ kind: 'xml', label: 'XML', color: '#10b981', meta: res.meta })
      else setFileBadge({ kind: 'invalid', label: 'Invalid XML', color: '#ef4444' })
      e.target.value = ''
      return
    }
    // Validate GeoJSON
    const gj = validateGeoJSON(text)
    if (gj.ok) setFileBadge({ kind: 'geojson', label: 'GEOJSON', color: '#6366f1', meta: gj.meta })
    else setFileBadge({ kind: 'invalid', label: 'Invalid GeoJSON', color: '#ef4444' })
    e.target.value = ''
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    if (!pwCurrent || !pwNew || !pwConfirm) { setMessage('Please fill all password fields.'); return }
    if (pwNew !== pwConfirm) { setMessage('New passwords do not match.'); return }
    if (pwNew.length < 8) { setMessage('New password must be at least 8 characters.'); return }
    try {
      const res = await fetchWithAuth('/api/user/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew })
      })
      if (res.ok) {
        setMessage('Password changed successfully.')
        setPwCurrent(''); setPwNew(''); setPwConfirm('')
      } else {
        const data = await res.json().catch(() => ({}))
        setMessage(data.error || 'Failed to change password.')
      }
    } catch {
      setMessage('Failed to change password.')
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-container settings-modal">
        <div className="modal-header settings-header">
          <h3>⚙️ Settings</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body settings-grid">
          <section className="settings-card">
            <details open>
            <summary className="settings-card__title" style={{ cursor: 'pointer' }}>🗺️ Site export & import</summary>
            <p className="settings-help">Export all assets and boundaries for a site or import a GeoJSON you exported earlier. Duplicates are skipped automatically.</p>
            <div className="settings-actions" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontWeight: 500 }}>Site</label>
              <select className="form-select settings-select" value={selectedSiteId} onChange={e => setSelectedSiteId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Select site…</option>
                {sites.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
              <button className="btn" onClick={fetchExportPreview} disabled={!selectedSiteId}>Preview</button>
              <button className="btn" onClick={downloadExport} disabled={!exportData}>Download</button>
              <label className="btn btn-primary" style={{ marginLeft: 'auto' }}>
                Import GeoJSON
                <input ref={importFileRef} type="file" accept=".geojson,application/geo+json,application/json" style={{ display: 'none' }} onChange={handleImportFile} />
              </label>
            </div>
            {exportData && (
              <div className="file-viewer" style={{ marginTop: '0.5rem' }}>
                <div className="file-viewer__header">Export Preview · {exportData.features?.length || 0} features</div>
                <div className="file-viewer__content">
                  {renderExportStats(exportData)}
                </div>
              </div>
            )}
            {importMessage && (
              <div className={importMessage.includes('Imported') ? 'success' : 'error'} style={{ marginTop: 8 }}>{importMessage}</div>
            )}
            </details>
          </section>

          <section className="settings-card">
            <details>
            <summary className="settings-card__title" style={{ cursor: 'pointer' }}>📥 File preview (GPX / GeoJSON)</summary>
            <p className="settings-help">Quickly inspect a GPX or GeoJSON file and, if applicable, view it on the map.</p>
            <div className="settings-actions" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={openFile}>Choose file…</button>
              <input ref={fileInputRef} type="file" accept=".geojson,.gpx" style={{ display: 'none' }} onChange={handleFileChange} />
              {fileBadge && (
                <span className="chip" style={{ background: badgeBg(fileBadge.color), borderColor: fileBadge.color }}>
                  {badgeIcon(fileBadge.kind)} {fileBadge.label}{fileBadge.meta ? ` · ${fileBadge.meta}` : ''}
                </span>
              )}
              {fileName && (
                <span style={{ marginLeft: '0.5rem', color: '#374151', fontSize: '0.9rem' }}>
                  {fileName}{fileContent ? ` · ${formatBytes(new Blob([fileContent]).size)}` : ''}
                </span>
              )}
              {fileBadge?.kind === 'gpx' && (
                <button className="btn" onClick={() => setShowGPXViewer(true)}>View on map</button>
              )}
              {fileBadge?.kind === 'geojson' && (
                <button className="btn" onClick={() => setShowGeoJSONViewer(true)}>View on map</button>
              )}
            </div>
            {fileContent && fileBadge?.kind !== 'gpx' && (
              <div className="file-viewer">
                <div className="file-viewer__header">{fileName}</div>
                <pre className="file-viewer__content">{escapeHtml(fileContent)}</pre>
              </div>
            )}
            </details>
          </section>

          <section className="settings-card settings-card--accent">
            <details>
            <summary className="settings-card__title" style={{ cursor: 'pointer' }}>🔒 Change password</summary>
            <p className="settings-help">Use a strong, unique password. Minimum 8 characters.</p>
            <form onSubmit={handleChangePassword} className="request-form">
              <div className="form-group">
                <label>Current password</label>
                <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} />
              </div>
              <div className="form-group">
                <label>New password</label>
                <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Confirm new password</label>
                <input type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} />
              </div>
              {message && <div className={message.includes('success') ? 'success' : 'error'}>{message}</div>}
              <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary">Change Password</button>
              </div>
            </form>
            </details>
          </section>

          <section className="settings-card">
            <details open>
              <summary className="settings-card__title" style={{ cursor: 'pointer' }}>ℹ️ About</summary>
              <div className="settings-help">Copyright © Optiarc Systems AB</div>
              {user.isAdmin && (
                <div className="settings-actions" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                  {uptime ? (
                    <div className="chip server-uptime" title="Server uptime">⏱ Uptime: {uptime}</div>
                  ) : null}
                  {version?.backend?.shortSha ? (
                    <div className="chip version-chip" title={version.backend.sha || undefined}>🖥 Backend: {version.backend.shortSha}</div>
                  ) : null}
                  {version?.frontend?.shortSha ? (
                    <div className="chip version-chip" title={version.frontend.sha || undefined}>🧩 Frontend: {version.frontend.shortSha}</div>
                  ) : null}
                </div>
              )}
            </details>
          </section>
          <section className="settings-card">
            <div className="settings-card__title">👤 Account</div>
            <div className="settings-actions" style={{ justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.fullName || user.username}
                    className="avatar"
                    style={{ width: 40, height: 40 }}
                    referrerPolicy="no-referrer"
                  />
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 600 }} title={user.fullName || user.username}>
                      {user.fullName || user.username}
                    </span>
                    {user.isAdmin ? (
                      <span className="chip" title="Administrator">🛡️ Admin</span>
                    ) : null}
                  </div>
                  <div style={{ color: '#4b5563' }} title={user.email}>
                    {user.email}
                  </div>
                </div>
              </div>
              <button type="button" className="btn logout-btn" onClick={logout} title="Logout">Logout</button>
            </div>
          </section>
        </div>
      </div>
      {showGPXViewer && (
        <GPXViewerModal gpxText={fileContent} onClose={() => setShowGPXViewer(false)} />
      )}
      {showGeoJSONViewer && (
        <GeoJSONViewerModal text={fileContent} onClose={() => setShowGeoJSONViewer(false)} />
      )}
    </div>
  )
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c] as string))
}

function validateGPX(text: string): { isXML: boolean; isGPX: boolean; meta?: string } {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(text, 'application/xml')
    const hasError = doc.getElementsByTagName('parsererror').length > 0
    if (hasError) return { isXML: false, isGPX: false }
    const root = doc.documentElement
    const rootTag = root?.tagName?.toLowerCase() || ''
    const isGPX = rootTag === 'gpx'
    // Collect meta: version, creator, wpt/trk counts
    let meta = ''
    if (isGPX) {
      const ver = root.getAttribute('version') || ''
      const creator = root.getAttribute('creator') || ''
      const wptCount = doc.getElementsByTagName('wpt').length
      const trkptCount = doc.getElementsByTagName('trkpt').length
      meta = `v${ver || '–'} · wpt ${wptCount} · trkpt ${trkptCount}${creator ? ` · ${creator}` : ''}`
    } else {
      meta = 'XML'
    }
    return { isXML: true, isGPX, meta }
  } catch {
    return { isXML: false, isGPX: false }
  }
}

function validateGeoJSON(text: string): { ok: boolean; meta?: string } {
  try {
    const obj = JSON.parse(text)
    if (!obj || typeof obj !== 'object') return { ok: false }
    const t = (obj.type || '').toUpperCase()
    const validTop = ['FEATURE', 'FEATURECOLLECTION', 'GEOMETRYCOLLECTION', 'POINT','MULTIPOINT','LINESTRING','MULTILINESTRING','POLYGON','MULTIPOLYGON']
    if (!validTop.includes(t)) return { ok: false }
    // Quick sanity checks: for Feature(s), geometry must exist
    if (t === 'FEATURE') {
      if (!obj.geometry || typeof obj.geometry !== 'object') return { ok: false }
    }
    if (t === 'FEATURECOLLECTION') {
      if (!Array.isArray(obj.features)) return { ok: false }
    }
    // Count features/coords to show meta
    let meta = t
    if (t === 'FEATURECOLLECTION') meta = `${t} · ${obj.features.length} features`
    return { ok: true, meta }
  } catch {
    return { ok: false }
  }
}

function badgeBg(color: string) {
  // light translucent bg based on color
  if (color === '#10b981') return 'rgba(16, 185, 129, 0.12)'
  if (color === '#6366f1') return 'rgba(99, 102, 241, 0.12)'
  if (color === '#ef4444') return 'rgba(239, 68, 68, 0.12)'
  return 'rgba(0,0,0,0.06)'
}

function badgeIcon(kind: string | null) {
  switch (kind) {
    case 'gpx': return '🗺️'
    case 'xml': return '🟢'
    case 'geojson': return '🟣'
    case 'invalid': return '⛔'
    default: return ''
  }
}

function formatBytes(n: number) {
  if (!Number.isFinite(n)) return ''
  if (n < 1024) return `${n} B`
  const kb = n/1024
  if (kb < 1024) return `${kb.toFixed(1)} kB`
  const mb = kb/1024
  return `${mb.toFixed(1)} MB`
}

function renderExportStats(fc: any) {
  try {
    const feats = Array.isArray(fc.features) ? fc.features : []
    const counts = feats.reduce((acc: any, f: any) => { const t = f?.properties?.featureType || 'unknown'; acc[t] = (acc[t]||0)+1; return acc }, {})
    const total = feats.length
    const bytes = new Blob([JSON.stringify(fc)]).size
    return (
      <div>
        <div>Total features: {total}</div>
        <div>Sites: {counts.site || 0} · Areas: {counts.area || 0} · Assets: {counts.asset || 0}</div>
        <div>Estimated size: {formatBytes(bytes)}</div>
      </div>
    )
  } catch {
    return <div>Unable to compute statistics.</div>
  }
}
